import { faArrowDownToBracket } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, EmptyState, Heading } from '@feathr/components';
import { useToggle } from '@feathr/hooks';
import { ExportModal } from '@feathr/report_components';

import type { IDashboardUrlState, TDateRange } from '../ReportsConversionsDashboardPage.utils';
import { useExport } from './AllConversionsTable.hooks';

import * as styles from './AllConversionsTable.css';

interface IProps {
  state: IDashboardUrlState;
  range: TDateRange;
}

/**
 * Shell + Export action.
 *
 * The row data, server-side pagination, attribution badge, and filter-empty
 * state are tracked in the follow-up integration ticket — BE work on §1, §6.
 * The Export action is wired to the existing `Conversions.export` endpoint
 * but won't deliver an email until BE §2 (account-scoped export) ships.
 */
function AllConversionsTable({ state, range }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [isExportOpen, toggleExport] = useToggle(false);
  const { handleExport } = useExport({ state, range });

  return (
    <>
      <Stack gap={theme.spacing['3']}>
        <div className={styles.headerActions}>
          <Heading order={2} variant={'h5'}>
            {t('All campaign conversions')}
          </Heading>
          <Button
            name={'export-conversions'}
            onClick={toggleExport}
            prefix={<FontAwesomeIcon icon={faArrowDownToBracket} />}
            type={'secondary'}
          >
            {t('Export')}
          </Button>
        </div>
        <EmptyState description={t('No conversions in this timeframe.')} />
      </Stack>
      {isExportOpen && (
        <ExportModal
          onClose={toggleExport}
          onConfirm={handleExport}
          title={t('Export conversions')}
        />
      )}
    </>
  );
}

export default AllConversionsTable;
